import React, { useCallback } from 'react';
import { observer } from 'mobx-react';
import * as yup from 'yup';
import { useParams } from 'react-router-dom';
import { DatePicker, Input, Spin, Switch, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useFormik } from 'formik';
import { useAuthStore, useLoyaltyBannerStore } from '../../contexts/mobx';
import { useUnsavedChanges } from '../../contexts/unsaved-changes';
import {
    LoyaltyBannerUpsertPayload,
    YupLoyaltyBannerUpsertPayload,
} from '../../stores/loyalty-banner';
import { useNavigation } from '../../utils/use-navigation';
import { DeleteButton, SaveButton } from '../../components/action-buttons';
import { ImageInput } from '../../components/image-input';
import { LabeledField } from '../../components/form-fields/labeled-field';
import { PageTopBar } from '../../components/page-top-bar';
import { Section } from '../../components/form-fields/section';
import { BackgroundColorPicker } from '../../components/background-color-picker';
import { IconUploader } from './components/IconUploader';
import { ApplicationTargetEnum } from '../../types/app-selection';
import { LoyaltyClubForPicker } from '../../stores/loyalty-banner';
import moment from 'moment';
import { LoyaltyClubPicker } from './components/LoyaltyClubPicker';
import style from './style.module.scss';

const YupParams = yup.object({
    action: yup.string().oneOf(['create', 'edit']).required(),
    id: yup
        .string()
        .test('shouldBeDefined', 'Id should be defined', function (v) {
            const action = this.resolve(yup.ref('action'));
            return !(v === undefined && action !== 'create');
        }),
});

type Params = yup.InferType<typeof YupParams>;

export const LoyaltyBannerPage = observer(() => {
    const params = useParams<Params>();
    const loyaltyBannerStore = useLoyaltyBannerStore();
    const navigation = useNavigation();
    const [isDeleting, setIsDeleting] = React.useState<boolean>(false);
    const [isFetching, setIsFetching] = React.useState<boolean>(false);
    const [isUpserting, setIsUpserting] = React.useState<boolean>(false);

    const authStore = useAuthStore();
    const { hasManagementAccess } = authStore;

    if (!hasManagementAccess) {
        navigation.goToHome();
    }

    const saveBanner = useCallback(
        async (values: LoyaltyBannerUpsertPayload) => {
            setIsUpserting(true);
            try {
                if (params.action === 'edit') {
                    await loyaltyBannerStore.updateLoyaltyBanner(
                        values,
                        Number(params.id!)
                    );
                } else {
                    await loyaltyBannerStore.createLoyaltyBanner(values);
                }
            } finally {
                setIsUpserting(false);
            }
        },
        [loyaltyBannerStore, params.action, params.id]
    );

    const formik = useFormik<LoyaltyBannerUpsertPayload>({
        initialValues: {
            isActive: false,
            loyaltyClubId: null,
            appTarget: ApplicationTargetEnum.QC,
            startDate: '',
            endDate: '',
            icon: null as string | null,
            translations: [
                {
                    title: '',
                    subtitle: '',
                    ctaText: '',
                    image: null,
                    backgroundColor: null,
                    language: 'en',
                },
                {
                    title: '',
                    subtitle: '',
                    ctaText: '',
                    image: null,
                    backgroundColor: null,
                    language: 'fr',
                },
            ],
        },
        validateOnMount: true,
        validationSchema: YupLoyaltyBannerUpsertPayload,
        onSubmit: saveBanner,
    });

    const { clearDirty } = useUnsavedChanges({
        dirty: formik.dirty,
        onSave: () => saveBanner(formik.values),
    });

    const createBanner = useCallback(async () => {
        try {
            await saveBanner(formik.values);
            clearDirty(() => navigation.replaceToLoyaltyBanners());
        } catch {
            // error notification already shown by store
        }
    }, [saveBanner, formik.values, clearDirty, navigation]);

    const updateBanner = useCallback(async () => {
        try {
            await saveBanner(formik.values);
            clearDirty();
        } catch {
            // error notification already shown by store
        }
    }, [saveBanner, formik.values, clearDirty]);

    if (!YupParams.isValidSync(params)) {
        navigation.replaceToLoyaltyBannerCreate();
    }

    React.useEffect(() => {
        if (params.action === 'edit') {
            setIsFetching(true);
            loyaltyBannerStore
                .fetchLoyaltyBanner(Number(params.id!))
                .then((banner) => {
                    formik.resetForm({ values: banner });
                })
                .catch(() => navigation.replaceToLoyaltyBannerCreate())
                .finally(() => setIsFetching(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onDeleteConfirm = async () => {
        setIsDeleting(true);
        const response = await loyaltyBannerStore.deleteLoyaltyBanner(
            Number(params.id!)
        );
        setIsDeleting(false);

        if (!response.err) {
            navigation.replaceToLoyaltyBanners();
        }
    };

    const renderTopBarActions = () => {
        switch (params.action) {
            case 'create':
                return (
                    <SaveButton
                        onClick={createBanner}
                        loading={isUpserting}
                        disabled={!formik.isValid}
                    />
                );
            case 'edit':
                return (
                    <>
                        <DeleteButton
                            onClick={onDeleteConfirm}
                            loading={isDeleting}
                        />
                        <SaveButton
                            onClick={updateBanner}
                            loading={isUpserting}
                            disabled={!formik.isValid}
                        />
                    </>
                );
            default:
                return null;
        }
    };

    return (
        <>
            <PageTopBar
                title={
                    params.action === 'edit' && params.id
                        ? `Loyalty Banner #${params.id}`
                        : 'New Loyalty Banner'
                }
                goBack={{
                    callback: () => navigation.goToLoyaltyBanners(),
                    text: 'Back to list',
                }}
            >
                {renderTopBarActions()}
            </PageTopBar>

            {isFetching ? (
                <Spin />
            ) : (
                <div className="content_with_margins">
                    <div className={style.promotion_page_content}>
                        <div className={style.left_side}>
                            <Section title="Config">
                                <div className={style.config_grid}>
                                    <LabeledField label="App Target">
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 8,
                                                padding: '5px 0',
                                            }}
                                        >
                                            <img
                                                loading="lazy"
                                                src="/ct-logo-xl.svg"
                                                alt=""
                                                style={{
                                                    width: 20,
                                                    height: 20,
                                                }}
                                            />
                                            <span>Couche-Tard</span>
                                        </div>
                                    </LabeledField>
                                    <LabeledField label="Loyalty Club">
                                        <LoyaltyClubPicker
                                            value={
                                                formik.values.loyaltyClubId ??
                                                null
                                            }
                                            onChange={(value) =>
                                                formik.setFieldValue(
                                                    'loyaltyClubId',
                                                    value
                                                )
                                            }
                                            onClubSelected={(
                                                club: LoyaltyClubForPicker
                                            ) => {
                                                if (
                                                    params.action === 'create'
                                                ) {
                                                    formik.setFieldValue(
                                                        'startDate',
                                                        club.registrationStartDate ??
                                                            ''
                                                    );
                                                    if (club.endDate) {
                                                        formik.setFieldValue(
                                                            'endDate',
                                                            club.endDate
                                                        );
                                                    }
                                                }
                                            }}
                                        />
                                    </LabeledField>
                                    <LabeledField
                                        label={
                                            <span>
                                                Promotional Icon{' '}
                                                <Tooltip title="24x24 SVG only">
                                                    <InfoCircleOutlined
                                                        className={
                                                            style.icon_label_info
                                                        }
                                                    />
                                                </Tooltip>
                                            </span>
                                        }
                                    >
                                        <IconUploader
                                            url={formik.values.icon}
                                            onChange={(url) =>
                                                formik.setFieldValue(
                                                    'icon',
                                                    url
                                                )
                                            }
                                            onDelete={() =>
                                                formik.setFieldValue(
                                                    'icon',
                                                    null
                                                )
                                            }
                                        />
                                    </LabeledField>
                                </div>
                            </Section>

                            <Section title="Schedule">
                                <div className={style.schedule_grid}>
                                    <LabeledField label="Start (Date & Time)">
                                        <DatePicker
                                            allowClear={false}
                                            showTime={{
                                                format: 'HH:mm',
                                                defaultValue: moment(
                                                    '00:00',
                                                    'HH:mm'
                                                ),
                                            }}
                                            format="YYYY-MM-DD HH:mm"
                                            value={
                                                formik.values.startDate
                                                    ? moment(
                                                          formik.values
                                                              .startDate
                                                      )
                                                    : null
                                            }
                                            onChange={(
                                                date: moment.Moment | null
                                            ) =>
                                                formik.setFieldValue(
                                                    'startDate',
                                                    date
                                                        ? date.toISOString()
                                                        : ''
                                                )
                                            }
                                        />
                                    </LabeledField>
                                    <LabeledField label="End (Date & Time)">
                                        <DatePicker
                                            allowClear={false}
                                            showTime={{
                                                format: 'HH:mm',
                                                defaultValue: moment(
                                                    '23:59',
                                                    'HH:mm'
                                                ),
                                            }}
                                            format="YYYY-MM-DD HH:mm"
                                            value={
                                                formik.values.endDate
                                                    ? moment(
                                                          formik.values.endDate
                                                      )
                                                    : null
                                            }
                                            onChange={(
                                                date: moment.Moment | null
                                            ) =>
                                                formik.setFieldValue(
                                                    'endDate',
                                                    date
                                                        ? date.toISOString()
                                                        : ''
                                                )
                                            }
                                        />
                                    </LabeledField>
                                </div>
                            </Section>

                            <Section title="Content">
                                <div className="columns_layout">
                                    {formik.values.translations.map(
                                        (item, i) => (
                                            <div
                                                className="column"
                                                key={item.language}
                                            >
                                                <div className="h5_bold">
                                                    {item.language === 'en'
                                                        ? 'English'
                                                        : 'French'}
                                                </div>
                                                <LabeledField
                                                    label={`Image (${item.language.toUpperCase()})`}
                                                >
                                                    <ImageInput
                                                        url={item.image}
                                                        onChange={(url) =>
                                                            formik.setFieldValue(
                                                                `translations[${i}].image`,
                                                                url
                                                            )
                                                        }
                                                        onDelete={() =>
                                                            formik.setFieldValue(
                                                                `translations[${i}].image`,
                                                                null
                                                            )
                                                        }
                                                    />
                                                </LabeledField>
                                                <BackgroundColorPicker
                                                    value={item.backgroundColor}
                                                    onChange={(value) =>
                                                        formik.setFieldValue(
                                                            `translations[${i}].backgroundColor`,
                                                            value
                                                        )
                                                    }
                                                    language={
                                                        item.language as
                                                            | 'en'
                                                            | 'fr'
                                                    }
                                                />
                                                <LabeledField
                                                    label={`Title (${item.language.toUpperCase()})`}
                                                >
                                                    <Input
                                                        name={`translations[${i}].title`}
                                                        value={item.title ?? ''}
                                                        onChange={
                                                            formik.handleChange
                                                        }
                                                    />
                                                </LabeledField>
                                                <LabeledField
                                                    label={`Subtitle (${item.language.toUpperCase()})`}
                                                >
                                                    <Input
                                                        name={`translations[${i}].subtitle`}
                                                        value={
                                                            item.subtitle ?? ''
                                                        }
                                                        onChange={
                                                            formik.handleChange
                                                        }
                                                    />
                                                </LabeledField>
                                                <LabeledField
                                                    label={`CTA Text (${item.language.toUpperCase()})`}
                                                >
                                                    <Input
                                                        name={`translations[${i}].ctaText`}
                                                        value={
                                                            item.ctaText ?? ''
                                                        }
                                                        onChange={
                                                            formik.handleChange
                                                        }
                                                    />
                                                </LabeledField>
                                            </div>
                                        )
                                    )}
                                </div>
                            </Section>
                        </div>

                        <div className={style.right_side}>
                            <Section title="Status">
                                <div className={style.status_column}>
                                    <div className={style.label_and_toggle}>
                                        <label className="footnote_description_bold">
                                            Active
                                        </label>
                                        <Switch
                                            checked={formik.values.isActive}
                                            onChange={(checked) =>
                                                formik.setFieldValue(
                                                    'isActive',
                                                    checked
                                                )
                                            }
                                        />
                                    </div>
                                    {formik.values.isActive && (
                                        <span className={style.active_warning}>
                                            Active banners with overlapping date
                                            ranges are not allowed.
                                        </span>
                                    )}
                                </div>
                            </Section>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
});
